import { Component, ViewChild } from '@angular/core';
import { NavController, NavParams } from 'ionic-angular';
import { DateService } from '../../../services/date/date.service';
import { GridComponent } from '@progress/kendo-angular-grid';
import { process, State } from '@progress/kendo-data-query';
import { weatherLogColumns } from './weather-log-component.config';
import { WeatherReadingsService } from '../../../services/weather-readings/weather-readings.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import {
  ProjectWeatherCompleteEditor,
  ProjectWeatherPrecipitationDetailsStatus,
  WeatherReading,
  WeatherRemarkGridRow,
  WeatherRiskOfLightningStatus,
  WeatherSetup
} from '../../../models/weather-readings/weather-readings.models';
import { ApiOption } from '../../../_foundation/models/base.models';
import { AppData } from '../../../models/app-data/app-data';
import { timer } from 'rxjs/observable/timer';
import { catchError, debounce } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { PageValidationType, ValidationResult } from '../../../models/projects/validation.models';
import { WeatherLogValidationService } from '../../../services/weather-readings/weather-log-validation.service';
import { VesselService } from '../../../services/vessel/vessel.service';
import { VesselOption } from '../../../models/setup/setup.models';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { CompleteDayFilters } from '../../../models/complete-day.models';
import { CompleteDayService } from '../../../services/complete-day/complete-day.service';
import { LockDayFilters } from '../../../models/lock-day.models';
import { ValidationService } from '../../../services/validation/validation.service';
import { weatherRemarkColumns } from './weather-remark-grid.config';
import { WeatherRemarkEditPage } from '../weather-remark-edit/weather-remark-edit';
import { LockedDayPermissions, WeatherPermissions } from '../../../models/permissions/permission.models';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { cloneDeep } from 'lodash';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { ImplementerService } from '../../../services/implementer/implementer.service';
import { DailyLogDatePickerComponent } from '../../shared/components/daily-log-date-picker/daily-log-date-picker';
import { AppConfig } from '../../../app/app.config';
import {
  DailyLegLoadsManageReadingsPage
} from '../../daily-leg-loads/daily-leg-load-manage-readings/daily-leg-load-manage-readings';
import { WeatherLogManageReadingsPage } from '../weather-log-manage-readings/weather-log-manage-readings';

@Component({
  selector: 'app-weather-log-component',
  templateUrl: 'weather-log-component.component.html'
})
export class WeatherLogComponent {
  @ViewChild('weatherLogGrid') grid?: GridComponent;
  @ViewChild('weatherRemarkGrid') remarkGrid?: GridComponent;
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  config = AppConfig.settings;
  weatherReadingData: WeatherReading[] = []; // one array for all data, to make validation easier, as we need to check all data before allowing user to mark complete
  selectedProjectWeatherReadingData: WeatherReading[] = [];
  weatherRemarkGridData: WeatherRemarkGridRow[];
  weatherSetups: WeatherSetup[];
  columns = weatherLogColumns;
  filteredColumns: any[] = [];
  state: State = {
    skip: 0,
    sort: [
      {
        field: 'sortTime',
        dir: 'asc'
      }],
  };

  remarkColumns = weatherRemarkColumns;
  remarkState: State = {
    take: 15,
  };

  dayCompleted: boolean;
  dayLockedByDate: boolean = false;
  isImplementerActiveForDay: boolean; // when true the day will be open for editing regardless of dayLockedByDate

  weatherCompleted: boolean = false;
  allReadingsComplete: boolean;
  showRemoveCustomButton: boolean;
  disableRows: boolean;
  currentSetupConfigured: boolean;
  selectedText: boolean;

  validationResult: ValidationResult = new ValidationResult();

  precipitationStatusOptions = ProjectWeatherPrecipitationDetailsStatus;
  precipitationOptions: ApiOption<number>[] = [];
  riskOfLightningStatusOptions = WeatherRiskOfLightningStatus;
  riskOfLightningOptions: ApiOption<number>[];

  loading: boolean;
  dataSyncing: boolean;
  latestTransactionId: number;
  initialLoad: boolean;
  firstLoadComplete: boolean = false;
  dateBeforeInitialSetup: boolean;
  validationLoading: boolean;
  dailyLogDate: PtLuxonDate = new PtLuxonDate();

  dataToUpdate: AppData = new AppData();
  $confirmedChange: Subject<AppData> = new Subject<AppData>();
  leavingPage: boolean = false;
  debounceActive = false;

  selectedProjectId: number;
  // because the tabs generated for kendo tabstrip are dynamic based on project changes - we need to keep track of the correct index to show the user
  selectedProjectIndex: number;
  vessel: string;

  permissions: WeatherPermissions = new WeatherPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public dateService: DateService,
    private weatherReadingService: WeatherReadingsService,
    private lockedDayService: LockedDayService,
    private dailyLogService: DailyLogService,
    private appDataSyncService: AppDataSyncService,
    private weatherLogValidatorService: WeatherLogValidationService,
    private vesselService: VesselService,
    private completeDayService: CompleteDayService,
    private validationService: ValidationService,
    private navHelperService: NavHelperService,
    private timeZoneService: TimeZoneService,
    private implementerService: ImplementerService,
    private permissionService: PermissionsService,
  ) {
    this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
      this.currentSetupConfigured = currentSetupConfigured;
    });

    this.navHelperService.updateWeatherLogRequiresReload(false);

    // reload will be required if the user has added or deleted an additional reading in the manage readings section
    this.navHelperService.weatherLogRequiresReload.subscribe(async requiresReload => {
      if (requiresReload) {
        await this.ngOnInit();
        this.navHelperService.updateWeatherLogRequiresReload(false);
      }
    });
  }

  async ngOnInit() {
    this.loading = true;
    this.initialLoad = true;
    this.validationLoading = true;
    this.latestTransactionId = 0;
    this.filteredColumns = [];

    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    this.permissions = await this.permissionService.weatherPermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);

    await this.appDataSyncService.updateWeatherAppDataForDate(this.dailyLogDate);

    await this.updateDataEntryVariables();
    this.createPrecipitationOptions();
    this.createRiskOfLightningOptions();


    this.vesselService.getEditor().then(async currentEditor => {
      this.vesselService.getVesselMetadata().then(metadata => {
        const updatedEditor = metadata.vesselOptions.filter(e => e.id === currentEditor.id)[0] || currentEditor;
        this.setVesselSpecificColumnHeaders(updatedEditor);
        this.vesselService.saveSetup(updatedEditor);
      });
    });

    this.updateWeatherSetup();

    this.$confirmedChange
      .pipe(
        catchError(err => {
          this.debounceActive = false;
          throw (err.error);
        }),
        debounce(() => timer(2000)))
      .subscribe(result => {
        if (!this.leavingPage) {
          this.dataSyncing = true;
          this.dataToUpdate.transactionId = this.latestTransactionId;
          this.weatherReadingService.quickSaveWeatherReadings(this.dataToUpdate.weatherReadingLogRecords)
            .then(async rec => {
              this.dataSyncing = false;
              this.debounceActive = false;
              this.validate();
              this.allReadingsComplete = this.weatherReadingData.every(e => e.statusId === 3);
              this.showRemoveCustomButton = this.weatherReadingData.some(e => e.isCustomTime);
              this.checkDayCompleted();
            });

          this.dataToUpdate = new AppData();
        }
      });
  }

  // one method for all - that checks dayCompleted, implementer status, etc, and sets canEnterData flag
  async updateDataEntryVariables() {
    await Promise.all([
      this.checkImplementerActive(),
      this.checkDayCompleted(),
      this.checkDayLocked(),
      await this.permissionService.updateLockedDayFunctionalPermissions(this.lockedDayPermissions, this.dailyLogDate)
    ]);

    await this.setCanEnterDataFlag();
  }

  async setCanEnterDataFlag() {
    if (!this.dayCompleted
      && ((this.config.isImplementerEnvironment && this.isImplementerActiveForDay) || (!this.config.isImplementerEnvironment && this.dayLockedByDate))) {
      this.navHelperService.updateCanEnterData(true);
    } else {
      this.navHelperService.updateCanEnterData(false);
    }
  }

  // used for when the user views the validation and then returns to the weather log - because leavingPage will be true if they view validation
  reEnterComponent() {
    this.leavingPage = false;
  }

  // this gets called via the ionViewWillLeave in the parent weather-log.ts
  // ensures that data gets saved correctly before the user leaves without having to wait for the debounce
  saveBeforeLeavingComponent() {
    this.leavingPage = true; // stop debounce from running after this save

    if (this.dataToUpdate.weatherReadingLogRecords && this.dataToUpdate.weatherReadingLogRecords.length > 0) {
      this.weatherReadingService.quickSaveWeatherReadings(this.dataToUpdate.weatherReadingLogRecords);
    }
  }

  // isImplementerEnvironment variable only tells us if the environment is the implementer, we still need to check if current date falls between
  // snapshotSummary start and end dates, if it does then we know this date is editable
  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  async completeWeatherLog() {
    const editor = new ProjectWeatherCompleteEditor();

    editor.date = this.dailyLogDate.dateTime;
    editor.isComplete = true;

    this.completeDayService.completeWeatherReadingDay(editor).then(result => {
      this.weatherCompleted = result.isComplete;
    });
  }

  async removeWeatherReadingComplete() {
    // this.editDayLoading = true;
    const weatherCompleteEditor = await this.completeDayService.setWeatherReadingDayNotComplete(this.dailyLogDate);
    if (weatherCompleteEditor) {
      this.weatherCompleted = weatherCompleteEditor.isComplete;
    }
  }

  async checkDayCompleted() {
    const filters = new CompleteDayFilters();

    filters.date = this.dailyLogDate.dateTime;

    this.dayCompleted = await this.completeDayService.checkDay(filters);
    this.weatherCompleted = await this.completeDayService.checkWeatherReadingDay(filters);
  }

  async checkDayLocked() {
    const filters = new LockDayFilters();

    filters.date = this.dailyLogDate.dateTime;

    const lockedSummary = await this.lockedDayService.checkDay(filters);
    this.dayLockedByDate = lockedSummary.lockedByDate;
  }

  async validate() {
    this.validationLoading = true;
    this.validationResult = new ValidationResult();

    await this.weatherReadingService.getWeatherLogDataForDay(this.dailyLogDate).then(async data => {
      await this.weatherLogValidatorService.validateWeatherLog(data).then(weatherValidation => {
        this.validationResult.errors = this.validationResult.errors.concat(weatherValidation.errors);
        this.validationLoading = false;
        this.loading = false;
      });
    });
  }

  async dayUnlockedForEditing() {
    this.removeWeatherReadingComplete();
    this.checkDayCompleted();
  }

  async changesSavedOnDatepicker() {
    await this.loadGrid();
  }

  async changesSaving() {
    this.disableRows = true;
    this.loading = true;
    this.dataSyncing = true;
    this.grid.loading = true;
  }

  async changesSaved() {
    this.loading = false;
    this.dataSyncing = false;
    this.grid.loading = false;
    this.disableRows = false;
  }

  async weatherReadingChanged(row: WeatherReading) {
    this.debounceActive = true;

    // set this regardless of if day is locked or not, the app was refactored to not re-update the griddata with data returned from api on save
    // so this needs to be set so that the recordedAtFields show correctly in the grid
    row = this.setVesselSpecificData(row);

    // set status - even though it will get set on the api save call and overwrite this, this will appear a lot faster for the user
    this.weatherReadingService.setStatuses([row]).then(async result => {
      row.statusId = result[0].statusId;

      if (result[0].statusId === (2 || 1)) {
        this.allReadingsComplete = false;
      }

      // need to replace the selected reading in weatherReadingData, or otherwise the validation won't know when this row is updated
      this.weatherReadingData.splice(this.weatherReadingData.findIndex(e => e.id === row.id), 1, row);

      await this.weatherReadingService.saveWeatherReadingMultipleRecords([cloneDeep(row)]);
      await this.sendToDebounce(row);
    });
  }

  async sendToDebounce(row: WeatherReading) {
    this.latestTransactionId++;

    const filteredRecords: WeatherReading[] = [];

    for (let i = 0; i < this.dataToUpdate.weatherReadingLogRecords.length; i++) {
      if (this.dataToUpdate.weatherReadingLogRecords[i].id !== row.id) {
        const sameDay = this.dateService.compareDayOffsetsPreApplied(this.dataToUpdate.weatherReadingLogRecords[i].date, row.date);
        if (sameDay) {
          filteredRecords.push(this.dataToUpdate.weatherReadingLogRecords[i]);
        }
      }
    }

    // offsets used to be applied to the dataToUpdate.dailyLegLoads here, but this was moved to just before the api call where it loops
    // through and does it all at once, it needed to move so that records that are edited get added to the array as soon as possible, so
    // if the user enters data and immediately clicks to reload the log, the data entered will be saved fast enough for it to be there when
    // the log reloads

    this.dataToUpdate.weatherReadingLogRecords = filteredRecords;
    this.dataToUpdate.weatherReadingLogRecords.push(cloneDeep(row));

    this.$confirmedChange.next(this.dataToUpdate);
  }

  checkColumnExistsOnSetup(column: any): boolean {
    const selectedWeatherSetup = this.weatherSetups.find(e => e.projectId === this.selectedProjectId);

    switch (column.field) {
      case 'precipitationDetailsId': {
        return selectedWeatherSetup.precipitationDetails === true;
      }
      case 'riskOfLightningId': {
        return selectedWeatherSetup.riskOfLightning === true;
      }
      case 'status':
      case 'timeShow':
        return true;
    }

    return selectedWeatherSetup[column.field] === true;
  }

  async loadGrid(transactionId?: number) {
    // this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    await this.checkDayCompleted();

    if (!this.initialLoad) {
      await this.appDataSyncService.updateWeatherAppDataForDate(this.dailyLogDate);
    }

    await this.loadRemarkGrid();

    this.weatherReadingService.getWeatherLogDataForDay(this.dailyLogDate).then(data => {
      // don't reload if we are waiting for another api call to finish
      if (transactionId && transactionId === this.latestTransactionId || !transactionId) {
        this.state.filter = {
          logic: 'and',
          filters: [
            {field: 'projectId', operator: 'eq', value: this.selectedProjectId}
          ]
        };

        this.weatherReadingData = cloneDeep(data);
        this.selectedProjectWeatherReadingData = (process(data, this.state)).data;

        this.initialLoad = false;
        this.firstLoadComplete = true;
        this.loading = false;
        this.allReadingsComplete = this.weatherReadingData.every(e => e.statusId === 3);
        this.showRemoveCustomButton = this.weatherReadingData.some(e => e.isCustomTime);
        this.debounceActive = false;
        this.dataSyncing = false;
        this.validate();
      }
    });
  }

  async dateChanged() {
    this.firstLoadComplete = false;
    this.weatherReadingData = [];
    this.selectedProjectWeatherReadingData = [];
    this.weatherRemarkGridData = [];
    this.weatherSetups = [];
    this.loading = true;
    this.dataSyncing = true;

    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    await this.updateDataEntryVariables();

    await this.appDataSyncService.updateWeatherAppDataForDate(this.dailyLogDate);

    this.updateWeatherSetup();
  }

  updateWeatherSetup() {
    this.weatherReadingService.getWeatherSetupsForDay(this.dailyLogDate).then(data => {
      if (data && data.length > 0) {
        this.weatherSetups = data;
        this.selectedProjectId = this.weatherSetups[0].projectId;
        this.selectedProjectIndex = 0;

        this.filteredColumns = this.columns.filter(e => {
          return this.checkColumnExistsOnSetup(e);
        });

        this.weatherSetups[0].customWeatherReadings.forEach(customReading => {
          const column = {
            field: null,
            custom: true,
            customId: customReading.id,
            title: customReading.columnName,
            filter: 'text',
            width: 100,
            minWidth: 100,
            mandatory: customReading.mandatory
          };

          this.filteredColumns.push(column);
        });

        this.weatherSetups[0].filteredColumns = this.filteredColumns;
      }

      this.loadGrid().then(() => {
        this.initialLoad = false;
      });
    });
  }

  async loadRemarkGrid() {
    if (this.remarkGrid) {
      this.remarkGrid.loading = true;
    }

    // this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    this.weatherReadingService.getWeatherRemarkEditorForDay(this.dailyLogDate, this.selectedProjectId).then(editor => {
      this.weatherRemarkGridData = [];
      if (editor && editor.weatherRemark && editor.weatherRemark.trim().length !== 0) {
        this.weatherRemarkGridData.push({
          weatherReportSection: 'Weather Remarks',
          comment: editor.weatherRemark,
          projectId: editor.projectId,
          date: editor.date,
          id: editor.id
        });
      }

      if (editor && editor.weatherForecast && editor.weatherForecast.trim().length !== 0) {
        this.weatherRemarkGridData.push({
          weatherReportSection: 'Weather Forecast for the Next 24 Hours',
          comment: editor.weatherForecast,
          projectId: editor.projectId,
          date: editor.date,
          id: editor.id
        });
      }

      if (this.remarkGrid) {
        this.remarkGrid.loading = false;
      }
    });
  }

  tabSelected(event: SelectEvent) {
    this.loading = true;
    const index = this.weatherSetups.findIndex(e => e.projectName === event.title);
    if (index !== -1) {
      this.weatherReadingData = [];
      this.selectedProjectWeatherReadingData = [];
      this.weatherRemarkGridData = [];
      this.selectedProjectId = this.weatherSetups[index].projectId;
      this.selectedProjectIndex = index;

      this.filteredColumns = this.columns.filter(e => {
        return this.checkColumnExistsOnSetup(e);
      });

      this.weatherSetups[index].customWeatherReadings.forEach(customReading => {
        const column = {
          field: null,
          custom: true,
          customId: customReading.id,
          title: customReading.columnName,
          filter: 'text',
          width: 100,
          minWidth: 100,
          mandatory: customReading.mandatory
        };

        this.filteredColumns.push(column);
      });

      this.weatherSetups[index].filteredColumns = this.filteredColumns;

      if (this.grid) {
        this.grid.loading = true;
      }

      this.loadGrid();
    }
  }

  // used to calculate the 'Recorded at' disabled fields when in locked day mode
  setVesselSpecificData(row: WeatherReading): WeatherReading {
    // rounding all to 2 decimal places
    row.windSpeedRecordedAt =
      Math.floor(((row.wind1RecordedAtCalculatedValue || 0) + (row.windSpeedRecordedAtAdjustment || 0)) * 100) / 100;
    row.windSpeed2RecordedAt =
      Math.floor(((row.wind2RecordedAtCalculatedValue || 0) + (row.windSpeed2RecordedAtAdjustment || 0)) * 100) / 100;
    row.windGustRecordedAt =
      Math.floor(((row.wind1RecordedAtCalculatedValue || 0) + (row.windGustRecordedAtAdjustment || 0)) * 100) / 100;
    row.windGust2RecordedAt =
      Math.floor(((row.wind2RecordedAtCalculatedValue || 0) + (row.windGust2RecordedAtAdjustment || 0)) * 100) / 100;
    row.averageWindSpeedRecordedAt =
      Math.floor(((row.wind1RecordedAtCalculatedValue || 0) + (row.averageWindSpeedRecordedAtAdjustment || 0)) * 100) / 100;
    row.averageWindSpeed2RecordedAt =
      Math.floor(((row.wind2RecordedAtCalculatedValue || 0) + (row.averageWindSpeed2RecordedAtAdjustment || 0)) * 100) / 100;

    return row;
  }

  columnRequired(column: any): boolean {
    if (column.field === 'status' || column.field === 'timeShow' || this.weatherSetups[0][column.field]) {
      return true;
    }

    return false;
  }

  columnMandatory(column: any): boolean {
    const selectedWeatherSetup = this.weatherSetups.find(e => e.projectId === this.selectedProjectId);

    switch (column.field) {
      case 'precipitationDetailsId': {
        return selectedWeatherSetup.precipitationDetailsMandatory === true;
      }
      case 'riskOfLightningId': {
        return selectedWeatherSetup.riskOfLightningMandatory === true;
      }
    }

    if (column.mandatory === true) {
      return true;
    }

    return selectedWeatherSetup[column.mandatory] === true;
  }

  setVesselSpecificColumnHeaders(vessel: VesselOption) {
    if (vessel.wind1RecordedAtLabel) {
      const speedIndex = this.columns.findIndex(e => e.field === 'windSpeedRecordedAt');
      this.columns[speedIndex].title = 'Wind Speed Recorded At ' + vessel.wind1RecordedAtLabel + ' (m)';
      const gustIndex = this.columns.findIndex(e => e.field === 'windGustRecordedAt');
      this.columns[gustIndex].title = 'Wind Gust Recorded At ' + vessel.wind1RecordedAtLabel + ' (m)';
      const avgIndex = this.columns.findIndex(e => e.field === 'averageWindSpeedRecordedAt');
      this.columns[avgIndex].title = 'Average Wind Speed Recorded At ' + vessel.wind1RecordedAtLabel + ' (m)';
    }

    if (vessel.wind2RecordedAtLabel) {
      const speedIndex = this.columns.findIndex(e => e.field === 'windSpeed2RecordedAt');
      this.columns[speedIndex].title = 'Wind Speed 2 Recorded At ' + vessel.wind2RecordedAtLabel + ' (m)';
      const gustIndex = this.columns.findIndex(e => e.field === 'windGust2RecordedAt');
      this.columns[gustIndex].title = 'Wind Gust 2 Recorded At ' + vessel.wind2RecordedAtLabel + ' (m)';
      const avgIndex = this.columns.findIndex(e => e.field === 'averageWindSpeed2RecordedAt');
      this.columns[avgIndex].title = 'Average Wind Speed 2 Recorded At ' + vessel.wind2RecordedAtLabel + ' (m)';
    }
  }

  createPrecipitationOptions() {
    this.precipitationOptions = [];

    this.precipitationOptions.push(
      {
        id: this.precipitationStatusOptions.Dry,
        name: 'Dry'
      },
      {
        id: this.precipitationStatusOptions.Drizzle,
        name: 'Drizzle'
      },
      {
        id: this.precipitationStatusOptions.Fog,
        name: 'Fog'
      },
      {
        id: this.precipitationStatusOptions.LightRain,
        name: 'Light Rain'
      },
      {
        id: this.precipitationStatusOptions.HeavyRain,
        name: 'Heavy Rain'
      },
      {
        id: this.precipitationStatusOptions.LightShowers,
        name: 'Light Showers'
      },
      {
        id: this.precipitationStatusOptions.HeavyShowers,
        name: 'Heavy Showers'
      },
      {
        id: this.precipitationStatusOptions.Hail,
        name: 'Hail'
      },
      {
        id: this.precipitationStatusOptions.Snow,
        name: 'Snow'
      }
    );
  }

  createRiskOfLightningOptions() {
    this.riskOfLightningOptions = [];

    this.riskOfLightningOptions.push(
      {
        id: this.riskOfLightningStatusOptions.Nil,
        name: 'Nil'
      },
      {
        id: this.riskOfLightningStatusOptions.Low,
        name: 'Low'
      },
      {
        id: this.riskOfLightningStatusOptions.Med,
        name: 'Med'
      },
      {
        id: this.riskOfLightningStatusOptions.High,
        name: 'High'
      }
    );
  }

  otherPagesNotComplete(): boolean {
    if (this.validationResult.errors.some(e => e.pageValidationType != null
      && e.pageValidationType !== PageValidationType.DailyObservations
      && e.pageValidationType !== PageValidationType.FieldPositioning
      && e.pageValidationType !== PageValidationType.VoyageLog)) {
      return true;
    }

    return false;
  }

  openWeatherRemarkEditor() {
    this.navCtrl.push(WeatherRemarkEditPage,
      {
        projectId: this.selectedProjectId
      });
  }

  getMinValue(allowNegative: any) {
    return allowNegative ? -9999 : 0;
  }

  // selectedText is used so we don't stop the user from being able to type in an input when they have text highlighted
  textSelected() {
    this.selectedText = true;
  }

  inputBlur() {
    this.selectedText = false;
  }

  keyPress(currentValue: number, event: KeyboardEvent, allowNegative: boolean, column?: any) {
    if (event.key === '-' && !allowNegative || (event.key === '.'
      && currentValue !== null && currentValue.toString().indexOf('.') !== -1)) {
      event.preventDefault();
    }

    switch (column.field) {
      case 'windSpeedRecordedAt':
      case 'windSpeed2RecordedAt':
      case 'windSpeed':
      case 'windSpeed2':
      case 'windGustRecordedAt':
      case 'windGust2RecordedAt':
      case 'windGust':
      case 'windGust2':
      case 'averageWindSpeedRecordedAt':
      case 'averageWindSpeed2RecordedAt':
      case 'averageWindSpeed':
      case 'averageWindSpeed2':
        if (currentValue != null) {
          const afterDecimal = currentValue.toString().split('.')[1];
          if (afterDecimal != null
            && afterDecimal.length >= 2
            && event.key !== 'Backspace'
            && event.key !== 'ArrowLeft'
            && event.key !== 'ArrowRight'
            && !this.selectedText) {
            event.preventDefault();
          }
        }
        break;
    }

    this.selectedText = false;
  }

  getCustomReadingId(row: any, column: any) {
    const index = row.customWeatherReadingInstances.findIndex(e => e.customWeatherReadingId === column.customId);

    if (index !== -1) {
      return index;
    }
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  dateLoadingFinished() {
    this.firstLoadComplete = true;
  }

  openManageReadingsPage() {
    this.navCtrl.push(WeatherLogManageReadingsPage);
  }
}



// WEBPACK FOOTER //
// ./src/pages/weather-readings/weather-log-component/weather-log-component.component.ts